/**
 * The round trip from New campaign to Contact lists and back (design sync 2026-10-09).
 *
 * The prototype's "Need a different audience? Create a contact list" opens Contact lists with the
 * new-list panel open and a banner — "Making a list for your new campaign…" — and saving there
 * ("Save and use in campaign") comes back to the campaign with the new list picked and the form as it
 * was left. Neither route's URL carries that: it rides in the browser's HISTORY STATE, so the router's
 * search validators (and their links) are unchanged, and a reload of either page keeps it.
 *
 * The state is read back defensively — it is whatever the history entry holds — and anything that is
 * not the shape below reads as "no hand-off".
 */
import type { HistoryState } from "@tanstack/react-router";

import type { QuestionEntry } from "./campaign-copy.js";
import type { CallingRangeView, ReturningPersonTreatmentView } from "../../api/gateway.js";

/** What the New campaign form had, so it comes back as it was left. */
export interface CampaignFormDraft {
  readonly chosenVoiceAgentId: string;
  readonly name: string;
  readonly returningPerson: ReturningPersonTreatmentView;
  readonly attempt: "1" | "2" | "3";
  readonly waitMinute: number;
  /** 2026-10-30: the calling ranges per weekday, as the form held them. */
  readonly callingHours: readonly CallingRangeView[];
  readonly simultaneousCall: number;
  readonly startDateText: string;
  readonly startTime: string;
  /** plan-015 CMP (G16): the last calling date as typed; "" is no end. */
  readonly endDateText: string;
  readonly callerId: string;
  readonly question: readonly QuestionEntry[];
}

/** Where Contact lists sends the member back to: the form's URL and what was on it. */
export interface CampaignReturn {
  /** The `voiceAgent` search value New campaign was opened with ("" when opened from a list). */
  readonly voiceAgentId: string;
  /** The list that was picked when the member left, for "Back to the campaign". */
  readonly contactListId: string;
  readonly draft: CampaignFormDraft;
}

const RETURN_KEY = "wetalkCampaignReturn";
const DRAFT_KEY = "wetalkCampaignDraft";

function isRecord(value: unknown): value is Readonly<Record<string, unknown>> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isText(value: unknown): value is string {
  return typeof value === "string";
}

function isQuestionEntry(value: unknown): value is QuestionEntry {
  return (
    isRecord(value) &&
    isText(value["key"]) &&
    isText(value["text"]) &&
    (value["kind"] === "scale" || value["kind"] === "choice" || value["kind"] === "open")
  );
}

function isCallingRange(value: unknown): value is CallingRangeView {
  return (
    isRecord(value) && typeof value["dayOfWeek"] === "number" && isText(value["fromLocalTime"]) && isText(value["toLocalTime"])
  );
}

function draftOf(value: unknown): CampaignFormDraft | null {
  if (!isRecord(value)) return null;
  const hours = value["callingHours"];
  const question = value["question"];
  const attempt = value["attempt"];
  const returningPerson = value["returningPerson"];
  if (
    !isText(value["chosenVoiceAgentId"]) ||
    !isText(value["name"]) ||
    !(returningPerson === "same_questions" || returningPerson === "different_questions" || returningPerson === "skip_them") ||
    !(attempt === "1" || attempt === "2" || attempt === "3") ||
    typeof value["waitMinute"] !== "number" ||
    !Array.isArray(hours) ||
    !hours.every(isCallingRange) ||
    typeof value["simultaneousCall"] !== "number" ||
    !isText(value["startDateText"]) ||
    !isText(value["startTime"]) ||
    !isText(value["endDateText"]) ||
    !isText(value["callerId"]) ||
    !Array.isArray(question) ||
    !question.every(isQuestionEntry)
  ) {
    return null;
  }
  return {
    chosenVoiceAgentId: value["chosenVoiceAgentId"],
    name: value["name"],
    returningPerson,
    attempt,
    waitMinute: value["waitMinute"],
    callingHours: hours.filter(isCallingRange),
    simultaneousCall: value["simultaneousCall"],
    startDateText: value["startDateText"],
    startTime: value["startTime"],
    endDateText: value["endDateText"],
    callerId: value["callerId"],
    question: question.filter(isQuestionEntry),
  };
}

/** The hand-off Contact lists was opened with, or `null`. */
export function campaignReturnOf(state: unknown): CampaignReturn | null {
  if (!isRecord(state)) return null;
  const value = state[RETURN_KEY];
  if (!isRecord(value) || !isText(value["voiceAgentId"]) || !isText(value["contactListId"])) return null;
  const draft = draftOf(value["draft"]);
  return draft === null ? null : { voiceAgentId: value["voiceAgentId"], contactListId: value["contactListId"], draft };
}

/** The form New campaign was sent back with, or `null`. */
export function campaignDraftOf(state: unknown): CampaignFormDraft | null {
  return isRecord(state) ? draftOf(state[DRAFT_KEY]) : null;
}

/** The history state that opens Contact lists for a campaign. */
export function withCampaignReturn(previous: HistoryState, handOff: CampaignReturn): HistoryState {
  return Object.assign({}, previous, { [RETURN_KEY]: handOff });
}

/** The history state that brings New campaign back as it was left. */
export function withCampaignDraft(previous: HistoryState, draft: CampaignFormDraft): HistoryState {
  return Object.assign({}, previous, { [RETURN_KEY]: undefined, [DRAFT_KEY]: draft });
}
